/**
 * PATH: apps/qa/src/lib/sync-service.ts
 * PURPOSE: Background sync service for offline-first operations
 *
 * WHY:
 * - Ensures data integrity between local IndexedDB and server
 * - Handles conflict resolution for concurrent edits
 * - Manages evidence upload queue with retry logic
 * - Provides real-time sync status updates to UI
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐    ┌─────────────┐
 *   │ Online      │───▶│ Process      │───▶│ Upload      │───▶│ Update      │
 *   │ Detected    │    │ Queue        │    │ Evidence    │    │ Local Cache │
 *   └─────────────┘    └──────────────┘    └─────────────┘    └─────────────┘
 *
 * DEPENDENCIES:
 *   - offline-db: IndexedDB wrapper
 *   - api: API client for server communication
 */

import { useState, useEffect } from 'react'
import api from '@/lib/api'
import offlineDb, {
  getPendingOperations,
  getPendingEvidence,
  updateOperationStatus,
  updateEvidenceStatus,
  deleteOperation,
  deleteEvidence,
  setLastSyncTime,
  getMetadata,
  setMetadata,
} from '@/lib/offline-db'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SyncStatus {
  isOnline: boolean
  isSyncing: boolean
  lastSyncTime: number | null
  pendingCount: number
  failedCount: number
  uploadingEvidence: number
}

type SyncStatusListener = (status: SyncStatus) => void

// ─────────────────────────────────────────────────────────────────────────────
// Sync Service Class
// ─────────────────────────────────────────────────────────────────────────────

class SyncService {
  private static instance: SyncService
  private listeners: Set<SyncStatusListener> = new Set()
  private isRunning = false
  private syncInterval: number | null = null
  private status: SyncStatus = {
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isSyncing: false,
    lastSyncTime: null,
    pendingCount: 0,
    failedCount: 0,
    uploadingEvidence: 0,
  }

  private constructor() {
    // Initialize online/offline listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.handleOnline())
      window.addEventListener('offline', () => this.handleOffline())
    }
  }

  static getInstance(): SyncService {
    if (!SyncService.instance) {
      SyncService.instance = new SyncService()
    }
    return SyncService.instance
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────────

  subscribe(listener: SyncStatusListener): () => void {
    this.listeners.add(listener)
    // Immediately notify with current status
    listener(this.status)
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener)
    }
  }

  async start(): Promise<void> {
    if (this.isRunning) return
    
    this.isRunning = true
    
    // Load last sync time from metadata
    const lastSync = await offlineDb.getLastSyncTime()
    this.updateStatus({ lastSyncTime: lastSync || null })
    
    // Initial sync if online
    if (this.status.isOnline) {
      await this.sync()
    }
    
    // Start periodic sync (every 30 seconds when online)
    this.syncInterval = window.setInterval(() => {
      if (this.status.isOnline && !this.status.isSyncing) {
        this.sync()
      }
    }, 30000)
  }

  stop(): void {
    this.isRunning = false
    if (this.syncInterval) {
      window.clearInterval(this.syncInterval)
      this.syncInterval = null
    }
  }

  async sync(): Promise<void> {
    if (!this.status.isOnline || this.status.isSyncing) return
    
    this.updateStatus({ isSyncing: true })
    
    try {
      // 1. Process pending operations
      await this.processOperationQueue()
      
      // 2. Upload pending evidence
      await this.uploadPendingEvidence()
      
      // 3. Fetch server updates (pull changes since last sync)
      await this.pullServerUpdates()
      
      // Update last sync time
      const now = Date.now()
      await setLastSyncTime(now)
      this.updateStatus({ lastSyncTime: now })
      
    } catch (error) {
      console.error('[SyncService] Sync failed:', error)
    } finally {
      // Update counts
      await this.refreshCounts()
      this.updateStatus({ isSyncing: false })
    }
  }

  async forceSyncNow(): Promise<void> {
    if (!this.status.isOnline) {
      throw new Error('Cannot sync while offline')
    }
    await this.sync()
  }

  getStatus(): SyncStatus {
    return { ...this.status }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Internal Methods
  // ─────────────────────────────────────────────────────────────────────────

  private handleOnline(): void {
    this.updateStatus({ isOnline: true })
    // Trigger immediate sync when coming back online
    this.sync()
  }

  private handleOffline(): void {
    this.updateStatus({ isOnline: false, isSyncing: false })
  }

  private updateStatus(partial: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...partial }
    this.notifyListeners()
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      try {
        listener(this.status)
      } catch (e) {
        console.error('[SyncService] Listener error:', e)
      }
    }
  }

  private async refreshCounts(): Promise<void> {
    const stats = await offlineDb.getStorageStats()
    this.updateStatus({
      pendingCount: stats.pendingOperations,
      failedCount: stats.failedOperations,
    })
  }

  private async processOperationQueue(): Promise<void> {
    const pendingOps = await getPendingOperations()

    for (const op of pendingOps) {
      try {
        await updateOperationStatus(op.id, 'in_progress')
        await this.executeOperation(op)
        await updateOperationStatus(op.id, 'synced')
        await deleteOperation(op.id)
      } catch (error: unknown) {
        console.error(`[SyncService] Operation ${op.id} failed:`, error)
        const err = error as { response?: { status?: number }; message?: string }
        const isConflict = err?.response?.status === 409
        const isPermanentFailure = err?.response?.status === 404 || err?.response?.status === 400

        if (isPermanentFailure && op.attempts >= 3) {
          await updateOperationStatus(op.id, 'failed', err.message)
        } else if (isConflict) {
          await updateOperationStatus(op.id, 'failed', 'Conflict: data was modified on server')
        } else {
          await updateOperationStatus(op.id, 'pending', err.message)
        }
      }
    }
  }

  private async executeOperation(op: Record<string, unknown>): Promise<void> {
    const { operationType, entityType, entityId, payload } = op
    
    switch (entityType) {
      case 'item_instance':
        if (operationType === 'update') {
          await api().post(`/qa/item-instances/${entityId}/set_status/`, payload)
        }
        break
        
      case 'evidence':
        // Evidence uploads are handled separately
        break
        
      case 'hold_point':
        if (operationType === 'create') {
          await api().post('/qa/hold-points/request/', payload)
        } else if (operationType === 'update') {
          await api().patch(`/qa/hold-points/${entityId}/`, payload)
        }
        break
        
      case 'ncr':
        if (operationType === 'create') {
          await api().post('/qa/ncrs/', payload)
        } else if (operationType === 'update') {
          await api().patch(`/qa/ncrs/${entityId}/`, payload)
        }
        break
        
      case 'area':
        if (operationType === 'update') {
          // Handle area lock/unlock
          if (payload.action === 'lock') {
            await api().post(`/qa/areas/${entityId}/lock/`, payload)
          } else if (payload.action === 'unlock') {
            await api().post(`/qa/areas/${entityId}/unlock/`, payload)
          }
        }
        break
        
      default:
        console.warn(`[SyncService] Unknown entity type: ${entityType}`)
    }
  }

  private async uploadPendingEvidence(): Promise<void> {
    const pendingEvidence = await getPendingEvidence()
    
    this.updateStatus({ uploadingEvidence: pendingEvidence.length })
    
    for (const evidence of pendingEvidence) {
      try {
        await updateEvidenceStatus(evidence.id, 'uploading')
        
        // 1. Upload the blob to files API
        const formData = new FormData()
        formData.append('file', evidence.blob, `evidence_${evidence.id}.jpg`)
        formData.append('purpose', 'qa_evidence')
        
        const uploadRes = await api().post('/files/upload/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const assetId = uploadRes.data.id
        
        // 2. Create evidence record linking to asset
        await api().post('/qa/evidence/', {
          area: evidence.areaId,
          item_instance: evidence.itemInstanceId,
          type: evidence.metadata.type,
          asset_id: assetId,
          description: evidence.metadata.description,
          device_metadata: {
            gps: evidence.metadata.gps,
            captured_at: evidence.metadata.timestamp,
            offline_id: evidence.id,
          },
        })
        
        // 3. Mark as uploaded and clean up
        await updateEvidenceStatus(evidence.id, 'uploaded', assetId)
        
        // Optionally delete local blob after successful upload
        await deleteEvidence(evidence.id)
        
        this.updateStatus({ uploadingEvidence: this.status.uploadingEvidence - 1 })
        
      } catch (error: unknown) {
        console.error(`[SyncService] Evidence upload ${evidence.id} failed:`, error)
        await updateEvidenceStatus(evidence.id, 'failed')
      }
    }
    
    this.updateStatus({ uploadingEvidence: 0 })
  }

  private async pullServerUpdates(): Promise<void> {
    // Get cursor from last successful sync
    const lastCursor = await getMetadata<string>('lastServerCursor')
    const deviceId = await this.getDeviceId()
    
    try {
      const response = await api().post('/qa/sync/', {
        device_id: deviceId,
        device_name: this.getDeviceName(),
        last_server_cursor: lastCursor || '',
        operations: [], // We've already pushed, just pulling now
      })
      
      const { server_cursor, updates } = response.data
      
      // Store new cursor
      if (server_cursor) {
        await setMetadata('lastServerCursor', server_cursor)
      }
      
      // Process server updates
      if (updates?.entities) {
        for (const entity of updates.entities) {
          await this.applyServerUpdate(entity)
        }
      }
      
    } catch (error) {
      console.error('[SyncService] Pull updates failed:', error)
      // Non-fatal, will retry next sync
    }
  }

  private async applyServerUpdate(entity: Record<string, unknown>): Promise<void> {
    const { type, id, data } = entity
    
    switch (type) {
      case 'inspection':
        await offlineDb.cacheInspection(id, data)
        break
      case 'area':
        if (data.inspection_id) {
          await offlineDb.cacheArea(id, data.inspection_id, data, data.items || [])
        }
        break
      // Add more entity types as needed
    }
  }

  private async getDeviceId(): Promise<string> {
    let deviceId = await getMetadata<string>('deviceId')
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
      await setMetadata('deviceId', deviceId)
    }
    return deviceId
  }

  private getDeviceName(): string {
    if (typeof navigator === 'undefined') return 'Unknown'
    
    const ua = navigator.userAgent
    if (/iPad/.test(ua)) return 'iPad'
    if (/iPhone/.test(ua)) return 'iPhone'
    if (/Android/.test(ua)) return 'Android Device'
    if (/Mac/.test(ua)) return 'Mac'
    if (/Windows/.test(ua)) return 'Windows PC'
    return 'Browser'
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Export singleton and hooks
// ─────────────────────────────────────────────────────────────────────────────

export const syncService = SyncService.getInstance()

// React hook for sync status
export function useSyncStatus(): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>(syncService.getStatus())
  
  useEffect(() => {
    const unsubscribe = syncService.subscribe(setStatus)
    return unsubscribe
  }, [])
  
  return status
}

export default syncService
